import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  PenSquare,
  MessageCircle,
  Lightbulb,
  BarChart3,
  FileText,
  LogOut,
  Menu,
  X,
  Shield,
  SmilePlus,
  Settings
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';
import { NotificationBell } from '@/components/NotificationBell';
import MoodCheckinModal from '@/components/MoodCheckinModal';
import BugReportButton from '@/components/BugReportButton';
import { moodsApi } from '@/lib/api';

export default function DashboardLayout() {
  const { user, logout, token } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showMoodModal, setShowMoodModal] = useState(false);

  useEffect(() => {
    const checkMoodStatus = async () => {
      if (sessionStorage.getItem('mood_checked')) return;
      try {
        const res = await moodsApi.getToday();
        if (!res.data.checked_in) {
          setShowMoodModal(true);
        }
      } catch (err) {
        console.error('Mood check failed:', err);
      }
    };
    checkMoodStatus();
  }, []);

  const navItems = [
    { path: '/dashboard', label: t('nav_dashboard'), icon: LayoutDashboard },
    { path: '/dashboard/mood', label: t('nav_mood'), icon: SmilePlus },
    { path: '/dashboard/journal', label: t('nav_journal'), icon: PenSquare },
    { path: '/dashboard/chat', label: t('nav_chat'), icon: MessageCircle },
    { path: '/dashboard/insights', label: t('nav_insights'), icon: Lightbulb },
    { path: '/dashboard/analytics', label: t('nav_analytics'), icon: BarChart3 },
    { path: '/dashboard/notes', label: t('nav_notes'), icon: FileText },
    { path: '/dashboard/settings', label: t('settings_title'), icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-navy-950">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 backdrop-blur-lg bg-navy-950/80 border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png" 
              alt="VerbNotes" 
              className="h-10"
            />
          </Link>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <LanguageSelector variant="ghost" />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-screen w-64 
          bg-navy-900/40 backdrop-blur-xl border-r border-white/5
          transition-transform duration-300 ease-in-out overflow-visible
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0`}
      >
        <div className="flex flex-col h-full p-4">
          {/* Logo - Bigger */}
          <Link to="/dashboard" className="flex items-center gap-2 mb-8 mt-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png" 
              alt="VerbNotes" 
              className="h-14"
            />
          </Link>

          {/* Desktop Header Controls */}
          <div className="hidden md:flex items-center gap-2 mb-4">
            <NotificationBell />
            <LanguageSelector />
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                    ${isActive 
                      ? 'bg-cyan/10 text-cyan border border-cyan/20' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="border-t border-white/5 pt-4 mt-4">
            <div className="px-4 py-2 mb-2 flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-cyan/20 text-cyan flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                  {(user?.name || user?.email || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm text-slate-400">{t('nav_signed_as')}</p>
                <p className="text-white font-medium truncate">{user?.name || user?.email}</p>
              </div>
            </div>
            {user?.is_admin && (
              <Link
                to="/admin"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-400/10 rounded-lg mb-1"
                data-testid="admin-panel-link"
              >
                <Shield size={18} />
                <span>Admin Panel</span>
              </Link>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start gap-3 text-slate-400 hover:text-red-400 hover:bg-red-400/10"
              data-testid="logout-btn"
            >
              <LogOut size={20} />
              <span>{t('nav_signout')}</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 pt-16 md:pt-0 min-h-screen">
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mood Check-in Modal */}
      <MoodCheckinModal
        isOpen={showMoodModal}
        onClose={() => setShowMoodModal(false)}
        onComplete={() => setShowMoodModal(false)}
      />

      {/* Floating Bug Report Button - Upper Right */}
      <div className="fixed top-3 right-4 md:top-4 md:right-6 z-[60]">
        <BugReportButton token={token} />
      </div>
    </div>
  );
}
